import Grid from '@mui/material/Grid';
import type { FunctionComponent } from 'react';
import { Fragment, useEffect, useState } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { Button } from '~/ui/core/button/Button';
import { StandaloneCard } from '~/ui/core/card/StandaloneCard';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { CustomizationInfo } from './app/app-bar/CustomizationInfo';
import { LanguageSelect } from './app/common/LanguageSelect';
import { route as dashboardV2Route } from './app/pages/dashboard-v2/constants';
import { LegacyOnboardingContainer } from './app/pages/onboarding/LegacyOnboardingContainer';
import { OnboardingButton } from './app/pages/onboarding/OnboardingButton';
import { OnboardingContainer } from './app/pages/onboarding/OnboardingContainer';
import { CompanyLogo } from './design/components/CompanyLogo';
import { Headline, SubTitle } from './design/components/Typography';

const defaultLoginRedirectPaths = new Set(['/', '/login', '/dashboard-v2']);
const legacyLoginRedirectPath = '/dashboard';

const getPostLoginCallbackUri = ({
    hash,
    pathname,
    search,
}: Pick<Location, 'hash' | 'pathname' | 'search'>): string => {
    if (defaultLoginRedirectPaths.has(pathname)) {
        return new URL(dashboardV2Route.patterns.dashboardV2, globalThis.location.origin).href;
    }

    return new URL(`${pathname}${search}${hash}`, globalThis.location.origin).href;
};

const LandingPageLegalText: FunctionComponent = () => {
    const { translate } = useApplicationContext();

    return (
        <p className="m-0 text-start text-body-xs font-normal text-text-content-subtle leading-[1.25rem]">
            {translate('pages.landing-page.legal.prefix')}
            <a
                className="font-medium text-text-primary underline"
                href="https://www.cobrainer.com/privacy"
                rel="noreferrer"
                target="_blank"
            >
                {translate('pages.landing-page.legal.privacy-policy')}
            </a>
            {translate('pages.landing-page.legal.suffix')}
        </p>
    );
};

type LoginProvider = {
    readonly index: number;
    readonly provider: {
        readonly loginUrl: URL;
    };
};

const LegacyLandingPage: FunctionComponent<{ readonly visibleProviders: readonly LoginProvider[] }> = ({
    visibleProviders,
}) => {
    const { actions, translate } = useApplicationContext();
    const legacyLoginCallbackUri = new URL(legacyLoginRedirectPath, globalThis.location.origin).href;

    return (
        <LegacyOnboardingContainer>
            <Grid alignItems="center" container={true} direction="column" rowGap={1.5}>
                <Grid>
                    <CompanyLogo />
                </Grid>
                <Grid>
                    <Headline variant="h4">{translate('pages.landing-page.title')}</Headline>
                </Grid>
                <Grid mb={5.5}>
                    <SubTitle>{translate('pages.landing-page.subtitle')}</SubTitle>
                </Grid>
                {visibleProviders.map(({ index, provider }) => (
                    <OnboardingButton
                        key={`${provider.loginUrl.href}-${index.toString()}`}
                        fullWidth={true}
                        label={translate(index !== 0 ? `common.login${(index + 1).toString()}` : 'common.login')}
                        onClick={() => {
                            actions.login(index, legacyLoginCallbackUri);
                        }}
                    />
                ))}
            </Grid>
        </LegacyOnboardingContainer>
    );
};

const LANDING_PAGE_VISITED_STORAGE_KEY = 'landing-page-visited';

const getHasVisitedLandingPage = (): boolean => {
    try {
        return globalThis.localStorage.getItem(LANDING_PAGE_VISITED_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const markLandingPageVisited = (): void => {
    try {
        globalThis.localStorage.setItem(LANDING_PAGE_VISITED_STORAGE_KEY, 'true');
    } catch {
        // localStorage not available, silently ignore
    }
};

const NewLandingPage: FunctionComponent<{ readonly visibleProviders: readonly LoginProvider[] }> = ({
    visibleProviders,
}) => {
    const [loggingInProviderIndex, setLoggingInProviderIndex] = useState<number | null>(null);
    const [hasVisitedBefore] = useState(getHasVisitedLandingPage);
    const {
        actions,
        applicationState: { location },
        translate,
    } = useApplicationContext();
    const postLoginCallbackUri = getPostLoginCallbackUri(location);

    useEffect(() => {
        markLandingPageVisited();
    }, []);

    const greeting = hasVisitedBefore
        ? translate('pages.landing-page.welcome-back')
        : translate('pages.landing-page.welcome');
    const tagline = translate('pages.landing-page.title');

    return (
        <OnboardingContainer>
            <div className="box-border flex h-full min-h-0 w-full max-w-[460px] flex-col gap-m  bg-transparent [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:w-full [&>*]:shrink-0">
                <section className="box-border flex min-h-[max(var(--spacing-8xl),calc(50%_-_var(--spacing-4xl)))] w-full flex-col justify-end gap-xs px-xxl pt-4xl pb-xl">
                    <div className="flex w-full justify-start">
                        <CompanyLogo />
                    </div>
                    <h1 className="m-0 text-h3 font-normal text-text-heading [view-transition-name:home-entry-title]">
                        {greeting}
                    </h1>
                    <p
                        aria-hidden={tagline.length === 0}
                        className="m-0 min-h-[1.25rem] text-body-s font-normal text-text-content-subtle [view-transition-name:home-entry-tagline]"
                    >
                        {tagline}
                    </p>
                </section>
                <div className="box-border flex w-full shrink-0 flex-col gap-m [&>*]:w-full">
                    <StandaloneCard className="w-full cursor-default gap-m p-xxl shadow-sm transition-shadow duration-200">
                        <div className="flex flex-col items-start gap-xs text-center w-full">
                            <h2 className="m-0 text-h3 font-normal text-text-heading [view-transition-name:home-entry-title]">
                                {translate('pages.landing-page.subtitle')}
                            </h2>
                        </div>
                        <div className="flex w-full flex-col gap-xxl pb-4xl pt-4xl">
                            {visibleProviders.map(({ index, provider }) => {
                                const isLoggingIn = loggingInProviderIndex === index;

                                return (
                                    <Button
                                        key={`${provider.loginUrl.href}-${index.toString()}`}
                                        iconPosition={isLoggingIn ? 'leading' : undefined}
                                        variant="primary"
                                        onClick={() => {
                                            setLoggingInProviderIndex(index);
                                            actions.login(index, postLoginCallbackUri);
                                        }}
                                    >
                                        {isLoggingIn ? <Spinner className="mr-0" /> : null}
                                        {translate(
                                            index !== 0 ? `common.login${(index + 1).toString()}` : 'common.login',
                                        )}
                                    </Button>
                                );
                            })}
                        </div>
                        <LandingPageLegalText />
                    </StandaloneCard>
                    <div className="flex justify-start">
                        <LanguageSelect variant="tertiary" />
                    </div>
                </div>
            </div>
        </OnboardingContainer>
    );
};

export const LandingPage: FunctionComponent = () => {
    const {
        applicationState: { customization },
    } = useApplicationContext();

    const providers = customization.isJust ? customization.value.oidcConfiguration.providers : [];
    const visibleProviders = providers
        .map((provider, index) => ({ index, provider }))
        .filter(({ provider }) => !provider.hideLoginButtons);
    const loginPageVariant = customization.mapOr('legacy', (value) => value.uiConfiguration.config.loginPage.variant);

    return (
        <Fragment>
            <CustomizationInfo />
            {loginPageVariant === 'new' ? (
                <NewLandingPage visibleProviders={visibleProviders} />
            ) : (
                <LegacyLandingPage visibleProviders={visibleProviders} />
            )}
        </Fragment>
    );
};
